import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { minutesToHours } from 'src/helpers/dateTimeFormatters';
import { getStatisticSummary } from 'src/helpers/statisticExtras';
import { TStatistic } from 'src/interfaces/Statistics';
import FieldSection from './FieldSection';
import InsightsPanelSurface from './InsightsPanelSurface';
import PerformanceSection from './PerformanceSection';
import PodiumSection from './PodiumSection';

interface Props {
  title: string;
  statistic: TStatistic;
  distanceKm?: number;
}

export default function DistanceInsightsPanel({ title, statistic, distanceKm }: Props) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const summary = getStatisticSummary(statistic);

  return (
    <InsightsPanelSurface sx={{ px: 2, pt: 1, pb: 2 }}>
      <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
          p: 1.5,
          borderRadius: 1,
          bgcolor: 'action.hover',
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('insights.hero.finishers')}
          </Typography>
          <Typography variant="h6">{summary.totalParticipants}</Typography>
        </Box>
        {summary.dnfCount && summary.dnfCount > 0 ? (
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('insights.hero.dnf')}
            </Typography>
            <Typography variant="h6">{summary.dnfCount}</Typography>
          </Box>
        ) : null}
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('insights.hero.fastest')}
          </Typography>
          <Typography variant="h6">
            {summary.fastestTime > 0 ? minutesToHours(summary.fastestTime, t) : '-'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('insights.hero.average')}
          </Typography>
          <Typography variant="h6">
            {summary.averageTime > 0 ? minutesToHours(summary.averageTime, t) : '-'}
          </Typography>
        </Box>
        {summary.medianTime > 0 ? (
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('insights.hero.median')}
            </Typography>
            <Typography variant="h6">{minutesToHours(summary.medianTime, t)}</Typography>
          </Box>
        ) : null}
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={t('insights.tabs.podium')} />
        <Tab label={t('insights.tabs.field')} />
        <Tab label={t('insights.tabs.performance')} />
      </Tabs>
      {tab === 0 ? <PodiumSection statistic={statistic} /> : null}
      {tab === 1 ? <FieldSection statistic={statistic} /> : null}
      {tab === 2 ? <PerformanceSection statistic={statistic} distanceKm={distanceKm} /> : null}
    </InsightsPanelSurface>
  );
}
